import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

export interface UserGoal {
  id: string;
  user_id: string;
  goal_type: string;
  goal_value?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateUserGoalInput {
  goal_type: string;
  goal_value?: string;
  is_active?: boolean;
}

export interface UpdateUserGoalInput {
  goal_type?: string;
  goal_value?: string;
  is_active?: boolean;
}

export type AnalyticsGoalMetric = 'followers' | 'reach' | 'engagement' | 'revenue';

export const ANALYTICS_GOAL_METRICS: AnalyticsGoalMetric[] = [
  'followers',
  'reach',
  'engagement',
  'revenue',
];

export const ANALYTICS_GOAL_PREFIX = 'analytics:';

export interface AnalyticsGoal {
  id: string;
  metric: AnalyticsGoalMetric;
  target: number;
  targetDate: string | null;
  createdAt: string;
}

export class UserGoalsService extends BaseService {
  private static instance: UserGoalsService;

  private constructor() {
    super();
  }

  public static getInstance(): UserGoalsService {
    if (!UserGoalsService.instance) {
      UserGoalsService.instance = new UserGoalsService();
    }
    return UserGoalsService.instance;
  }

  /**
   * Get all active goals for the current user
   */
  async getUserGoals(): Promise<ServiceResponse<UserGoal[]>> {
    const cacheKey = 'user-goals';

    return this.executeWithCache(
      cacheKey,
      async () => {
        const user = await this.getCurrentUser();
        if (!user) {
          throw new ServiceError('User not authenticated', 'UNAUTHORIZED');
        }

        const { data, error } = await this.client
          .from('user_goals')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) {
          throw new ServiceError(`Failed to fetch user goals: ${error.message}`, 'DATABASE_ERROR');
        }

        return data || [];
      },
      { ttl: 5 * 60 * 1000 },
    ); // 5 minute cache
  }

  /**
   * Create a new user goal
   */
  async createUserGoal(goalData: CreateUserGoalInput): Promise<ServiceResponse<UserGoal>> {
    const user = await this.getCurrentUser();
    if (!user) {
      return { error: new ServiceError('User not authenticated', 'UNAUTHORIZED'), data: null };
    }

    try {
      const { data, error } = await this.client
        .from('user_goals')
        .insert({
          user_id: user.id,
          goal_type: goalData.goal_type,
          goal_value: goalData.goal_value ?? null,
          is_active: goalData.is_active ?? true,
        })
        .select()
        .single();

      if (error) {
        throw new ServiceError(`Failed to create user goal: ${error.message}`, 'DATABASE_ERROR');
      }

      // Clear cache
      this.clearCache('user-goals');

      return { data, error: null };
    } catch (err) {
      const error =
        err instanceof ServiceError
          ? err
          : new ServiceError(`Failed to create user goal: ${err}`, 'UNKNOWN_ERROR');
      return { error, data: null };
    }
  }

  /**
   * Update multiple user goals (deactivate existing and create new ones)
   */
  async updateUserGoals(goalTypes: string[]): Promise<ServiceResponse<UserGoal[]>> {
    const user = await this.getCurrentUser();
    if (!user) {
      return { error: new ServiceError('User not authenticated', 'UNAUTHORIZED'), data: null };
    }

    try {
      // First, deactivate all existing goals
      const { error: deactivateError } = await this.client
        .from('user_goals')
        .update({ is_active: false })
        .eq('user_id', user.id);

      if (deactivateError) {
        throw new ServiceError(
          `Failed to deactivate existing goals: ${deactivateError.message}`,
          'DATABASE_ERROR',
        );
      }

      // Then create new goals if any are provided
      if (goalTypes.length === 0) {
        // Clear cache and return empty array
        this.clearCache('user-goals');
        return { data: [], error: null };
      }

      const goalInserts = goalTypes.map((goalType) => ({
        user_id: user.id,
        goal_type: goalType,
        title: goalType, // Using goal_type as title
        is_active: true,
      }));

      const { data, error } = await this.client.from('user_goals').insert(goalInserts).select();

      if (error) {
        throw new ServiceError(`Failed to create new goals: ${error.message}`, 'DATABASE_ERROR');
      }

      // Clear cache
      this.clearCache('user-goals');

      return { data: data || [], error: null };
    } catch (err) {
      const error =
        err instanceof ServiceError
          ? err
          : new ServiceError(`Failed to update user goals: ${err}`, 'UNKNOWN_ERROR');
      return { error, data: null };
    }
  }

  /**
   * Deactivate all user goals
   */
  async deactivateAllGoals(): Promise<ServiceResponse<boolean>> {
    const user = await this.getCurrentUser();
    if (!user) {
      return { error: new ServiceError('User not authenticated', 'UNAUTHORIZED'), data: null };
    }

    try {
      const { error } = await this.client
        .from('user_goals')
        .update({ is_active: false })
        .eq('user_id', user.id);

      if (error) {
        throw new ServiceError(`Failed to deactivate goals: ${error.message}`, 'DATABASE_ERROR');
      }

      // Clear cache
      this.clearCache('user-goals');

      return { data: true, error: null };
    } catch (err) {
      const error =
        err instanceof ServiceError
          ? err
          : new ServiceError(`Failed to deactivate goals: ${err}`, 'UNKNOWN_ERROR');
      return { error, data: null };
    }
  }

  /**
   * Delete a specific user goal
   */
  async deleteUserGoal(goalId: string): Promise<ServiceResponse<boolean>> {
    const user = await this.getCurrentUser();
    if (!user) {
      return { error: new ServiceError('User not authenticated', 'UNAUTHORIZED'), data: null };
    }

    try {
      const { error } = await this.client
        .from('user_goals')
        .delete()
        .eq('id', goalId)
        .eq('user_id', user.id);

      if (error) {
        throw new ServiceError(`Failed to delete user goal: ${error.message}`, 'DATABASE_ERROR');
      }

      // Clear cache
      this.clearCache('user-goals');

      return { data: true, error: null };
    } catch (err) {
      const error =
        err instanceof ServiceError
          ? err
          : new ServiceError(`Failed to delete user goal: ${err}`, 'UNKNOWN_ERROR');
      return { error, data: null };
    }
  }

  // ===== Analytics metric goals =====
  // Reuses user_goals via a namespaced goal_type ('analytics:<metric>') and the
  // REAL columns only (goal_value text + target_date).

  async getAnalyticsGoals(): Promise<ServiceResponse<AnalyticsGoal[]>> {
    const user = await this.getCurrentUser();
    if (!user) {
      return { error: new ServiceError('User not authenticated', 'UNAUTHORIZED'), data: null };
    }
    try {
      const { data, error } = await this.client
        .from('user_goals')
        .select('id, goal_type, goal_value, target_date, created_at')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .like('goal_type', `${ANALYTICS_GOAL_PREFIX}%`)
        .order('created_at', { ascending: true });
      if (error) {
        throw new ServiceError(
          `Failed to fetch analytics goals: ${error.message}`,
          'DATABASE_ERROR',
        );
      }
      const goals = (data ?? [])
        .map((r) => ({
          id: r.id as string,
          metric: (r.goal_type as string).slice(
            ANALYTICS_GOAL_PREFIX.length,
          ) as AnalyticsGoalMetric,
          target: Number(r.goal_value ?? 0),
          targetDate: (r.target_date as string | null) ?? null,
          createdAt: r.created_at as string,
        }))
        .filter((g) => ANALYTICS_GOAL_METRICS.includes(g.metric) && g.target > 0);
      return { data: goals, error: null };
    } catch (err) {
      const error =
        err instanceof ServiceError
          ? err
          : new ServiceError(`Failed to fetch analytics goals: ${err}`, 'UNKNOWN_ERROR');
      return { error, data: null };
    }
  }

  /** Set (or replace) the goal for a metric. One active goal per metric. */
  async upsertAnalyticsGoal(
    metric: AnalyticsGoalMetric,
    target: number,
    targetDate: string | null,
  ): Promise<ServiceResponse<boolean>> {
    const user = await this.getCurrentUser();
    if (!user) {
      return { error: new ServiceError('User not authenticated', 'UNAUTHORIZED'), data: null };
    }
    const goalType = `${ANALYTICS_GOAL_PREFIX}${metric}`;
    try {
      await this.client
        .from('user_goals')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('goal_type', goalType)
        .eq('is_active', true);

      const { error } = await this.client.from('user_goals').insert({
        user_id: user.id,
        goal_type: goalType,
        goal_value: String(Math.round(target)),
        target_date: targetDate,
        is_active: true,
      });
      if (error) {
        throw new ServiceError(`Failed to set analytics goal: ${error.message}`, 'DATABASE_ERROR');
      }
      return { data: true, error: null };
    } catch (err) {
      const error =
        err instanceof ServiceError
          ? err
          : new ServiceError(`Failed to set analytics goal: ${err}`, 'UNKNOWN_ERROR');
      return { error, data: null };
    }
  }
}

// Export singleton instance
export const userGoalsService = UserGoalsService.getInstance();
